import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Heart, Music, Trash2, Play } from 'lucide-react';
import { getInitialStations } from '@/constants/stations';
import { useAudioStore } from '@/hooks/useAudioStore';
import { useAudioPlayer } from '@/hooks/useAudioPlayer';
import { fetchStreams } from '@/services/strapi';
import { getStationDisplayName } from '@/utils/stationName';
import type { RadioStation } from '@/types';

export const FavoritesScreen: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage || i18n.language || 'de';
  const [streams, setStreams] = useState<RadioStation[]>(() => getInitialStations());
  const { favorites, removeFavorite, currentStation, isPlaying } = useAudioStore();
  const { playStation } = useAudioPlayer();

  useEffect(() => {
    fetchStreams(locale).then((s) => s.length > 0 && setStreams(s));
  }, [locale]);

  const favoriteStations = React.useMemo(() => {
    return streams.filter((station) => favorites.includes(station.id));
  }, [streams, favorites]);

  const handleClearAll = () => {
    favorites.forEach((id) => removeFavorite(id));
  };

  return (
    <div className="min-h-screen px-4 pt-6 pb-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between mb-6"
      >
        <div className="flex items-center gap-3">
          <Heart className="w-7 h-7 text-accent-pink fill-accent-pink" />
          <h1 className="text-3xl font-bold">{t('favorites.title')}</h1>
        </div>
        {favorites.length > 0 && (
          <button
            onClick={handleClearAll}
            className="flex items-center gap-2 px-3 py-2 rounded-full glass text-error hover:bg-error/20 transition-colors"
          >
            <Trash2 className="w-4 h-4" />
            <span className="text-sm">{t('favorites.clearAll')}</span>
          </button>
        )}
      </motion.div>

      {/* Stats */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="glass rounded-2xl p-6 flex justify-around mb-6"
      >
        <div className="text-center">
          <p className="text-3xl font-bold text-accent-blue">{favorites.length}</p>
          <p className="text-sm text-text-secondary">{t('favorites.stations')}</p>
        </div>
        <div className="w-px bg-white/10" />
        <div className="text-center">
          <p className="text-3xl font-bold text-accent-cyan">
            {Math.floor(favorites.length * 2.5)}
          </p>
          <p className="text-sm text-text-secondary">{t('favorites.hoursListening')}</p>
        </div>
      </motion.div>

      {/* Favorites Grid */}
      {favoriteStations.length > 0 ? (
        <div className="grid grid-cols-2 gap-4">
          {favoriteStations.map((station, index) => {
            const isCurrentStation = currentStation?.id === station.id;
            const isCurrentPlaying = isCurrentStation && isPlaying;

            return (
              <motion.div
                key={station.id}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.1 + index * 0.05 }}
                className="glass rounded-2xl overflow-hidden"
              >
                <div
                  className="relative cursor-pointer"
                  onClick={() => {
                    playStation(station);
                    navigate('/app/player');
                  }}
                >
                  <img
                    src={station.artwork}
                    alt={station.name}
                    className="w-full aspect-square object-cover"
                  />
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      removeFavorite(station.id);
                    }}
                    className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/50 flex items-center justify-center hover:bg-accent-pink/80 transition-colors"
                  >
                    <Heart className="w-4 h-4 fill-current" />
                  </button>
                </div>
                <div className="p-3">
                  <h3 className="font-semibold text-sm truncate">{getStationDisplayName(station.name)}</h3>
                  <p className="text-xs text-text-muted">{station.genre}</p>
                  <button
                    onClick={() => {
                      playStation(station);
                      navigate('/app/player');
                    }}
                    className={`mt-2 w-full py-2 rounded-lg flex items-center justify-center gap-2 text-sm transition-colors ${
                      isCurrentPlaying
                        ? 'bg-accent-blue text-white'
                        : 'glass hover:bg-white/10'
                    }`}
                  >
                    <Play className={`w-4 h-4 ${isCurrentPlaying ? 'fill-current' : ''}`} />
                    {isCurrentPlaying ? t('favorites.playing') : t('favorites.play')}
                  </button>
                </div>
              </motion.div>
            );
          })}
        </div>
      ) : (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="glass rounded-3xl p-12 text-center"
        >
          <Music className="w-16 h-16 text-text-muted mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2">{t('favorites.empty')}</h2>
          <p className="text-text-secondary">
            {t('favorites.emptyHint')}
          </p>
        </motion.div>
      )}
    </div>
  );
};
